import React from 'react';
import { Plus, Trash2, Loader2, Save } from 'lucide-react';
import { BriefQuestion, BriefChampLie } from './BriefQuestionsForm';

/**
 * Éditeur de questions pour UN modèle de Brief — délibérément plus simple que celui des Démarches client
 * (src/components/Settings/DocumentTemplatesSettings.tsx) : pas de blocage conditionnel, pas d'enchaînement
 * vers une autre Démarche, pas de paiement. Le Brief est un outil interne rempli après coup par la
 * commerciale, pas un parcours envoyé au client (demande d'Emeni, 2026-09-01) — ces mécanismes n'ont aucun
 * sens ici et n'auraient fait qu'ajouter de la complexité inutile.
 */
const TYPES: { value: BriefQuestion['type']; label: string }[] = [
    { value: 'text', label: 'Texte libre' },
    { value: 'select', label: 'Choix' },
    { value: 'datetime', label: 'Date et heure' },
    { value: 'deroule', label: 'Déroulé horaire' },
];

/** Champs de la privatisation qu'une réponse peut pré-remplir, selon le type de question — voir
 *  `BriefChampLie` et `CreerPrivatisationPanel.tsx`. Rien pour 'select'/'deroule' : un choix fermé ou une
 *  liste de moments ne sont pas des valeurs uniques exploitables pour une date/heure/nombre. */
const CHAMPS_LIABLES: Partial<Record<BriefQuestion['type'], { value: BriefChampLie; label: string }[]>> = {
    text: [{ value: 'num_people', label: 'Nombre de couverts' }],
    datetime: [
        { value: 'date', label: 'Date de la privatisation' },
        { value: 'start_time', label: 'Heure de début' },
        { value: 'end_time', label: 'Heure de fin' },
    ],
};

let compteur = 0;
const nouvelId = () => `q${Date.now()}_${compteur++}`;

export function BriefTemplateEditor({ initial, saving, error, onCancel, onSave }: {
    initial?: { id: string; name: string; questions: BriefQuestion[]; is_active: boolean } | null;
    saving: boolean;
    error: string;
    onCancel: () => void;
    onSave: (payload: { name: string; questions: BriefQuestion[]; is_active: boolean }) => void;
}) {
    const [name, setName] = React.useState(initial?.name || '');
    const [actif, setActif] = React.useState(initial?.is_active ?? true);
    const [questions, setQuestions] = React.useState<BriefQuestion[]>(initial?.questions || []);

    const ajouter = () => setQuestions((prev) => [...prev, {
        id: nouvelId(), question: '', type: 'text', options: [], allow_multiple: false, required: false, help_text: '', champ_lie: null,
    }]);
    const modifier = (id: string, patch: Partial<BriefQuestion>) =>
        setQuestions((prev) => prev.map((q) => q.id === id ? { ...q, ...patch } : q));
    const retirer = (id: string) => setQuestions((prev) => prev.filter((q) => q.id !== id));

    const ajouterOption = (qId: string) => setQuestions((prev) => prev.map((q) => q.id === qId
        ? { ...q, options: [...q.options, { id: nouvelId(), label: '' }] } : q));
    const modifierOption = (qId: string, oId: string, label: string) => setQuestions((prev) => prev.map((q) => q.id === qId
        ? { ...q, options: q.options.map((o) => o.id === oId ? { ...o, label } : o) } : q));
    const retirerOption = (qId: string, oId: string) => setQuestions((prev) => prev.map((q) => q.id === qId
        ? { ...q, options: q.options.filter((o) => o.id !== oId) } : q));

    const nomManquant = !name.trim();
    const questionsValides = questions.filter((q) => q.question.trim());
    const bloque = nomManquant || saving;

    const champ = 'w-full bg-surface border border-border rounded-lg px-2.5 py-1.5 text-sm text-content outline-none focus:border-brand-500';

    return (
        <div className="space-y-4">
            <div className="space-y-1">
                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Nom du modèle *</label>
                <input value={name} onChange={(e) => setName(e.target.value)} className={champ} autoFocus placeholder="ex : Rendez-vous privatisation" />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={actif} onChange={(e) => setActif(e.target.checked)} className="accent-brand-600 w-3.5 h-3.5" />
                <span className="text-xs font-bold text-content">Actif — choisissable dans l'onglet Brief d'un dossier</span>
            </label>

            <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-content">Questions</span>
                    <button onClick={ajouter} type="button" className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-[11px]">
                        <Plus size={12} /> Ajouter
                    </button>
                </div>

                {questions.length === 0 && (
                    <p className="text-[11px] text-content-muted">Aucune question pour l'instant.</p>
                )}

                {questions.map((q) => (
                    <div key={q.id} className="p-2.5 rounded-lg bg-surface-soft border border-border space-y-2">
                        <div className="flex items-center gap-1.5">
                            <input type="text" value={q.question} onChange={(e) => modifier(q.id, { question: e.target.value })}
                                placeholder="Intitulé de la question" className={champ} />
                            <button onClick={() => retirer(q.id)} type="button" className="text-red-400 shrink-0"><Trash2 size={13} /></button>
                        </div>
                        <div className="flex items-center gap-1 flex-wrap">
                            {TYPES.map((t) => (
                                <button key={t.value} type="button"
                                    onClick={() => modifier(q.id, { type: t.value, champ_lie: t.value === q.type ? q.champ_lie : null })}
                                    className={`px-2 py-1 rounded-md text-[10px] font-bold border transition-colors ${q.type === t.value ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'bg-surface border-border text-content-muted hover:text-content'}`}>
                                    {t.label}
                                </button>
                            ))}
                        </div>
                        {/* Pré-remplissage automatique de la privatisation depuis cette réponse — demande
                            d'Emeni (2026-09-02) : « nombre de personnes », « heure de début »... sont déjà
                            connus pendant le brief, pas besoin de les ressaisir à la création. */}
                        {CHAMPS_LIABLES[q.type] && (
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Pré-remplir automatiquement</label>
                                <select value={q.champ_lie || ''} onChange={(e) => modifier(q.id, { champ_lie: (e.target.value || null) as BriefChampLie | null })}
                                    className={champ}>
                                    <option value="">Aucun</option>
                                    {CHAMPS_LIABLES[q.type]!.map((c) => {
                                        const dejaUtilise = questions.some((autre) => autre.id !== q.id && autre.champ_lie === c.value);
                                        return (
                                            <option key={c.value} value={c.value} disabled={dejaUtilise}>
                                                {c.label}{dejaUtilise ? ' (déjà utilisé par une autre question)' : ''}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        )}
                        {q.type === 'select' && (
                            <div className="space-y-1.5 pl-2 border-l-2 border-border">
                                {q.options.map((o) => (
                                    <div key={o.id} className="flex items-center gap-1.5">
                                        <input type="text" value={o.label} onChange={(e) => modifierOption(q.id, o.id, e.target.value)}
                                            placeholder="Réponse possible" className={champ} />
                                        <button onClick={() => retirerOption(q.id, o.id)} type="button" className="text-red-400 shrink-0"><Trash2 size={12} /></button>
                                    </div>
                                ))}
                                <button onClick={() => ajouterOption(q.id)} type="button" className="text-[10px] font-bold text-brand-600 dark:text-brand-400">
                                    + Ajouter une réponse
                                </button>
                            </div>
                        )}
                        <div className="flex items-center gap-3">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                                <input type="checkbox" checked={q.required} onChange={(e) => modifier(q.id, { required: e.target.checked })} className="accent-brand-600 w-3.5 h-3.5" />
                                <span className="text-[11px] text-content-muted">Obligatoire</span>
                            </label>
                        </div>
                        <textarea value={q.help_text || ''} onChange={(e) => modifier(q.id, { help_text: e.target.value })} rows={1}
                            placeholder="Aide affichée sous la question (facultatif)"
                            className="w-full bg-surface border border-border rounded-lg px-2.5 py-1.5 text-[11px] text-content outline-none focus:border-brand-500 resize-none" />
                    </div>
                ))}
            </div>

            {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                <button onClick={onCancel} type="button" className="px-3 py-1.5 rounded-lg text-xs font-bold text-content-muted hover:text-content">Annuler</button>
                <button onClick={() => onSave({ name: name.trim(), questions: questionsValides, is_active: actif })} type="button" disabled={bloque}
                    className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-brand-fg text-xs font-bold">
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Enregistrer
                </button>
            </div>
        </div>
    );
}
